import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container, Alert, Button, Typography, Box } from '@mui/material';
import { applyActionCode } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import PageHeader from '../../../components/PageHeader';

/**
 * VerifyEmailHandler - Handles email verification from Firebase email links
 */
export default function VerifyEmailHandler({ 
    oobCode, 
    apiKey, 
    continueUrl, 
    onSuccess, 
    onError 
}) {
    const [isVerifying, setIsVerifying] = useState(false);
    const [verified, setVerified] = useState(false);

    const styles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
    };

    useEffect(() => {
        verifyEmail();
    }, []);

    const verifyEmail = async () => {
        setIsVerifying(true);
        try {
            const auth = getAuth();
            await applyActionCode(auth, oobCode);
            setVerified(true);
        } catch (error) {
            console.error('Email verification failed:', error);
            
            let errorMessage = 'Email verification failed.';
            
            switch (error.code) {
                case 'auth/expired-action-code':
                    errorMessage = 'This verification link has expired. Please request a new one.';
                    break;
                case 'auth/invalid-action-code':
                    errorMessage = 'This verification link is invalid.';
                    break;
                case 'auth/user-disabled':
                    errorMessage = 'This account has been disabled.';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found for this verification link.';
                    break;
                default:
                    errorMessage = `Email verification failed: ${error.message}`;
            }
            
            onError(errorMessage);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleContinue = () => {
        if (continueUrl) {
            window.location.href = continueUrl;
        } else {
            onSuccess('Your email has been verified successfully.');
        }
    };

    if (isVerifying) {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Verifying Email...' variant='h4' />
                <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                    Please wait while we verify your email address.
                </Typography>
            </Container>
        );
    }

    if (verified) {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Email Verified!' variant='h4' />
                <Alert severity="success" sx={{ mt: 2, width: '100%', maxWidth: 400 }}>
                    Your email address has been successfully verified.
                </Alert>
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleContinue}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Continue to Application
                    </Button>
                </Box>
            </Container>
        );
    }

    return null;
}

VerifyEmailHandler.propTypes = {
    oobCode: PropTypes.string.isRequired,
    apiKey: PropTypes.string,
    continueUrl: PropTypes.string,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
